import { useContext } from 'react';

import { CageContext } from './CageContext';
import type { CageContextValue } from './types';

/**
 * The selectors below are for readability, not for fewer renders: React context has no selector
 * granularity, so every consumer of `CageContext` re-renders when the provider's value identity
 * changes, whether it called `useCage()` or `useCageName()`.
 *
 * The provider builds its value in a single `useMemo`, which gives one rule:
 *
 *   A selector may only return a field of the memoised value, or a primitive derived from it.
 *   It may never build an object or array literal — that would hand every consumer a fresh
 *   identity on every render.
 *
 * That is why there is no `useCageLicenses()` aggregate: it would have to return
 * `{ hasCageLicense, hasLicenseByCode }`. Both closures are already stable inside the memo, so
 * destructuring them off `useCage()` is both cheaper and clearer.
 */

/** Throws outside a `CageProvider`: every cage-scoped screen mounts under one. */
export function useCage(): CageContextValue {
    const context = useContext(CageContext);

    if (context == null) {
        throw new Error(
            'useCage must be used within a CageProvider. ' +
                'Make sure CagePageWrapper is mounted higher in the component tree.'
        );
    }

    return context;
}

/**
 * TODO: Delete this hook. It exists because the export molecule has a strong dependency on
 * cage context data while also being rendered outside cage routes. Names should be threaded
 * through the DownloadOptions / DownloadCsvXlsx / useDownloadCSVXLSX signatures instead.
 */
export function useCageOptional(): CageContextValue | undefined {
    return useContext(CageContext);
}

/** Always set, including while loading and on error — it comes from the provider prop. */
export function useCageId(): number {
    return useCage().cageId;
}

export function useCageLoading(): boolean {
    return useCage().loading;
}

/** `undefined` until the query resolves. Callers that need a string supply their own fallback. */
export function useCageName(): string | undefined {
    return useCage().cage?.name;
}

/**
 * Raw pass-through with no `'UTC'` default: several call sites apply their own fallback, and
 * inserting one here would silently change what they render.
 */
export function useCageTimezone(): string | undefined {
    return useCage().location?.timezone;
}
